import React, { useEffect, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import { useNotify } from 'react-admin'
import { IProductLine, setContract } from '../../../Services/productEntitlements'
import { ProductLinesTable } from '../BillingAccountProducts/ProductLinesTable'
import CheckIcon from '@mui/icons-material/Check'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SyncIcon from '@mui/icons-material/Sync'
import {
	ensureStripeCustomer,
	IStripeInvoice,
	listStripeInvoices,
	markInvoicePaidManually,
	resyncStripe,
} from '../../../Services/stripeAdmin'

interface Props {
	billingAccount: any
	onChanged?: () => void
	/** Les lignes produit du compte quand Stripe les pilote (lecture seule ici). */
	productLines?: IProductLine[]
	onProductsChanged?: () => void
	/** Compte sous contrat : ce bloc ne s'affiche alors que parce qu'il reste
	 *  quelque chose chez Stripe, et ce quelque chose demande une décision. */
	underContract?: boolean
}

const formatAmount = (amount: number, currency: string) => {
	try {
		return new Intl.NumberFormat('fr-FR', {
			style: 'currency',
			currency: currency.toUpperCase(),
		}).format(amount / 100)
	} catch {
		return `${amount / 100} ${currency.toUpperCase()}`
	}
}

const formatDate = (ts?: number) => (ts ? new Date(ts * 1000).toLocaleDateString('fr-FR') : '—')

const statusColor = (s: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
	switch (s) {
		case 'paid':
			return 'success'
		case 'open':
			return 'warning'
		case 'void':
		case 'uncollectible':
			return 'error'
		case 'draft':
			return 'info'
		default:
			return 'default'
	}
}

export const BillingAccountStripe: React.FC<Props> = ({ billingAccount, onChanged, productLines, onProductsChanged, underContract }) => {
	const notify = useNotify()
	const [invoices, setInvoices] = useState<IStripeInvoice[]>([])
	const [loadingInvoices, setLoadingInvoices] = useState(false)
	const [linking, setLinking] = useState(false)
	const [resyncing, setResyncing] = useState(false)
	const [linkExistingOpen, setLinkExistingOpen] = useState(false)
	const [contracting, setContracting] = useState(false)

	const toContract = async () => {
		setContracting(true)
		try {
			await setContract(billingAccount.id)
			notify('Compte passé sous contrat : les produits se gèrent désormais dans le bloc Produits du contrat', { type: 'success' })
			onProductsChanged?.()
		} catch (e: any) {
			notify(e?.message || 'Échec du passage sous contrat', { type: 'error' })
		} finally {
			setContracting(false)
		}
	}

	const hasCustomer = !!billingAccount?.stripId
	const subscriptions: any[] = billingAccount?.subscriptions || []

	const loadInvoices = async () => {
		if (!hasCustomer) return
		setLoadingInvoices(true)
		try {
			const list = await listStripeInvoices(billingAccount.id)
			setInvoices(list || [])
		} catch (e: any) {
			notify(e?.message || 'Failed to load invoices', { type: 'error' })
		} finally {
			setLoadingInvoices(false)
		}
	}

	useEffect(() => {
		loadInvoices()
	}, [billingAccount?.id, hasCustomer])

	const handleCreateCustomer = async () => {
		setLinking(true)
		try {
			await ensureStripeCustomer(billingAccount.id)
			notify('Stripe customer created', { type: 'success' })
			onChanged?.()
		} catch (e: any) {
			notify(e?.message || 'Failed to create customer', { type: 'error' })
		} finally {
			setLinking(false)
		}
	}

	const handleResync = async () => {
		setResyncing(true)
		try {
			await resyncStripe(billingAccount.id)
			notify('Synced from Stripe', { type: 'success' })
			loadInvoices()
			onChanged?.()
			onProductsChanged?.() // les produits dérivent de la sub : à relire aussi
		} catch (e: any) {
			notify(e?.message || 'Failed to resync', { type: 'error' })
		} finally {
			setResyncing(false)
		}
	}

	const handleMarkPaid = async (invoiceId: string) => {
		try {
			await markInvoicePaidManually(invoiceId)
			notify('Invoice marked as paid', { type: 'success' })
			loadInvoices()
		} catch (e: any) {
			notify(e?.message || 'Failed to mark invoice as paid', { type: 'error' })
		}
	}

	return (
		<Card style={{ marginBottom: 20 }}>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Stripe
				</Typography>
				{underContract && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						Ce compte est <strong>sous contrat</strong> : il se facture dans Pennylane. Ce qui reste ici est un résidu — un
						abonnement encore actif prélèverait le client <strong>en plus</strong> de sa facture. À résilier dans Stripe une
						fois le contrat en place.
					</Alert>
				)}

				<Stack spacing={2} sx={{ mb: 3 }}>
					<Box>
						<Typography variant="subtitle2" gutterBottom>
							Customer
						</Typography>
						{hasCustomer ? (
							<Stack direction="row" spacing={1} alignItems="center">
								<Chip label={billingAccount.stripId} color="success" size="small" />
								<Button
									size="small"
									endIcon={<OpenInNewIcon />}
									href={`https://dashboard.stripe.com/customers/${billingAccount.stripId}`}
									target="_blank"
								>
									Open in Stripe
								</Button>
							</Stack>
						) : (
							<Stack direction="row" spacing={1}>
								<Button variant="contained" size="small" disabled={linking} onClick={handleCreateCustomer}>
									{linking ? 'Creating…' : 'Create in Stripe'}
								</Button>
								<Button variant="outlined" size="small" onClick={() => setLinkExistingOpen(true)}>
									Link existing (cus_…)
								</Button>
							</Stack>
						)}
					</Box>

					{hasCustomer && (
						<Box>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="subtitle2">Abonnements Stripe</Typography>
								<Button
									variant="outlined"
									size="small"
									startIcon={<SyncIcon />}
									disabled={resyncing}
									onClick={handleResync}
								>
									{resyncing ? 'Syncing…' : 'Resync from Stripe'}
								</Button>
							</Stack>

							{subscriptions.length === 0 && (
								<Alert severity="warning">
									Aucune subscription active. Crée la sub dans le{' '}
									<a
										href={`https://dashboard.stripe.com/customers/${billingAccount.stripId}`}
										target="_blank"
										rel="noreferrer"
									>
										Stripe Dashboard
									</a>{' '}
									puis clique "Resync".
								</Alert>
							)}

							{subscriptions.length > 0 && (
								<Stack spacing={2}>
									{subscriptions.map((sub: any) => (
										<Card key={sub.id} variant="outlined">
											<CardContent sx={{ pb: 1 }}>
												<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
													<Chip
														label={sub.status}
														size="small"
														color={sub.status === 'active' ? 'success' : 'warning'}
													/>
													{sub.currentPeriodEnd && (
														<Typography variant="caption" color="text.secondary">
															{sub.cancelAtPeriodEnd ? 'prend fin le' : 'renouvellement le'}{' '}
															{new Date(sub.currentPeriodEnd).toLocaleDateString('fr-FR')}
														</Typography>
													)}
													<Box sx={{ flex: 1 }} />
													<Button
														size="small"
														endIcon={<OpenInNewIcon />}
														href={`https://dashboard.stripe.com/subscriptions/${sub.stripeSubscriptionId}`}
														target="_blank"
													>
														Modifier dans Stripe
													</Button>
													{productLines && (
														<Button
															variant="outlined"
															size="small"
															disabled={contracting}
															onClick={toContract}
															title="Les 6 produits passent sous contrat (Pennylane) : quantités indicatives, non bloquantes, non écrasées par Stripe"
														>
															Passer sous contrat (Entreprise)
														</Button>
													)}
												</Stack>
												{productLines && (
													<>
														<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
															Produits de l’abonnement — Quantité = contractuelle, lue dans Stripe (bloquante) · Usage = consommation réelle.
															Un produit absent de l’abonnement n’ouvre aucun accès.
														</Typography>
														<ProductLinesTable lines={productLines} />
													</>
												)}
											</CardContent>
										</Card>
									))}
								</Stack>
							)}
						</Box>
					)}

				</Stack>

				{hasCustomer && (
					<Box>
						<Typography variant="subtitle2" gutterBottom>
							Factures
						</Typography>
						{loadingInvoices && <CircularProgress size={20} />}
						{!loadingInvoices && invoices.length === 0 && (
							<Alert severity="info">Aucune facture pour l’instant.</Alert>
						)}
						{!loadingInvoices && invoices.length > 0 && (
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Number</TableCell>
										<TableCell>Created</TableCell>
										<TableCell>Due</TableCell>
										<TableCell>Amount</TableCell>
										<TableCell>Status</TableCell>
										<TableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									{invoices.map((inv) => (
										<TableRow key={inv.id}>
											<TableCell>{inv.number || inv.id}</TableCell>
											<TableCell>{formatDate(inv.created)}</TableCell>
											<TableCell>{formatDate(inv.due_date)}</TableCell>
											<TableCell>{formatAmount(inv.amount_due, inv.currency)}</TableCell>
											<TableCell>
												<Chip label={inv.status} size="small" color={statusColor(inv.status)} />
											</TableCell>
											<TableCell align="right">
												<Stack direction="row" spacing={1} justifyContent="flex-end">
													{inv.hosted_invoice_url && (
														<Button
															size="small"
															endIcon={<OpenInNewIcon />}
															href={inv.hosted_invoice_url}
															target="_blank"
														>
															View
														</Button>
													)}
													{inv.status === 'open' && (
														<Button
															size="small"
															variant="contained"
															color="success"
															startIcon={<CheckIcon />}
															onClick={() => handleMarkPaid(inv.id)}
														>
															Mark as paid
														</Button>
													)}
												</Stack>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</Box>
				)}

				<LinkExistingCustomerDialog
					open={linkExistingOpen}
					onClose={() => setLinkExistingOpen(false)}
					billingAccountId={billingAccount.id}
					onLinked={() => onChanged?.()}
				/>
			</CardContent>
		</Card>
	)
}

interface LinkExistingProps {
	open: boolean
	onClose: () => void
	billingAccountId: string
	onLinked: () => void
}

const LinkExistingCustomerDialog: React.FC<LinkExistingProps> = ({ open, onClose, billingAccountId, onLinked }) => {
	const notify = useNotify()
	const [value, setValue] = useState('')
	const [submitting, setSubmitting] = useState(false)

	const submit = async () => {
		if (!value.startsWith('cus_')) {
			notify('Stripe customer ID must start with "cus_"', { type: 'warning' })
			return
		}
		setSubmitting(true)
		try {
			await ensureStripeCustomer(billingAccountId, value.trim())
			notify('Stripe customer linked', { type: 'success' })
			setValue('')
			onLinked()
			onClose()
		} catch (e: any) {
			notify(e?.message || 'Failed to link', { type: 'error' })
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle>Link existing Stripe customer</DialogTitle>
			<DialogContent>
				<TextField
					label="Stripe customer ID"
					placeholder="cus_..."
					value={value}
					onChange={(e) => setValue(e.target.value)}
					fullWidth
					autoFocus
					sx={{ mt: 1 }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button variant="contained" onClick={submit} disabled={submitting}>
					{submitting ? 'Linking…' : 'Link'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
